import React, { FC, useMemo } from 'react';
import styles from './styles.module.css';
import {
  ConnectedIntegration,
  WebIntegration,
  HarvestIntegration
} from './types';
import { Integration } from './types';
import { Button } from 'antd';
import * as translations from './strings';
import { getLangKey } from '../../models/Language';
import {
  IntegrationMessage,
  useIntegrationMessages
} from './integrationMessages';

const strings = translations[getLangKey()];
import Card from './Card';

type TaskConnectionData = {
  id: number;
  remoteTask: string;
  meta: Record<string, any>;
  projectIntegrationId: number;
};

type Props = {
  connectedIntegrations: ConnectedIntegration[];
  harvestIntegration: HarvestIntegration;
  container: HTMLDivElement;
  projectId: number;
  bugherdUrl: string;
  apiDomain?: string;
  isAdminView?: boolean;
  taskId: number;
  taskLocalId: number;
  isSmallView?: boolean;
  webIntegrations?: WebIntegration[];
  harvestEmbedUrl: string;
  organizationId: number;
  taskConnectionData: TaskConnectionData[];
};

// TODO: these probably can be consolidated into a few objects... we need to get this prop drilling under control
const Integrations: FC<Props> = ({
  connectedIntegrations,
  harvestIntegration,
  harvestEmbedUrl,
  container,
  projectId,
  isSmallView,
  bugherdUrl,
  apiDomain,
  isAdminView,
  taskId,
  taskLocalId,
  webIntegrations,
  organizationId,
  taskConnectionData
}) => {
  const nativeIntegrations = useMemo(() => {
    return connectedIntegrations
      .filter(integration => integration.connector.code !== 'team')
      .filter(integration => integration.projectIntegration)
      .map(integration => {
        const matchingTaskConnections = taskConnectionData?.filter(
          task =>
            task.projectIntegrationId === integration.projectIntegration?.id
        );
        const matchingTaskConnection =
          matchingTaskConnections?.find(task => task.remoteTask) ||
          matchingTaskConnections?.[0];

        return {
          ...integration,
          taskConnectionId: matchingTaskConnection?.id,
          remoteTaskId: matchingTaskConnection?.remoteTask || '',
          remoteTaskMeta: matchingTaskConnection?.meta || {}
        };
      });
  }, [connectedIntegrations, taskConnectionData]);
  const {
    messages: integrationMessages,
    loading: integrationMessagesLoading,
    refresh: refreshIntegrationMessages
  } = useIntegrationMessages(apiDomain || bugherdUrl, projectId, taskId);

  const getLinkText = (projectName: string): string => {
    if (!projectName) return '';
    const match = projectName.match(/ (\()(.*?)(\))/g);
    const hasHash = projectName[0] === '#';
    return match
      ? (hasHash ? '' : '#') +
          projectName.slice(0, projectName.indexOf(match[0]))
      : projectName;
  };

  const getProjectName = (
    code: string,
    remoteProject: string,
    remoteProjectName: string,
    settings: Object
  ) => {
    if (code === 'slack_legacy') return getLinkText(remoteProjectName);
    if (code === 'github') return remoteProject || '';
    if (code === 'slack') return `#${settings['channelId']?.['label'] || ''}`;
    return remoteProjectName || '';
  };

  const harvestEnabled = !!harvestIntegration?.id;

  const showIntegrations =
    connectedIntegrations.length ||
    webIntegrations?.length ||
    harvestEnabled;

  const handleShowSettings = () => {
    if (isAdminView) {
      // @ts-ignore
      if (window?.integration_settings_props?.setShow) {
        window.integration_settings_props.setShow(true);
      }
    } else {
      window.open(
        `${bugherdUrl}/projects/${projectId}/kanban?open=integration_settings`,
        '_blank'
      );
    }
  };

  type CardProps = {
    cardKey: string;
    integration?: Integration;
    error?: {
      code: number;
      message: string;
      created_at: string;
    };
    hasQueryWorkflow?: boolean;
    loading?: boolean;
    projectName?: string;
    showHelp?: boolean;
    harvestEmbedUrl?: string;
    projectIntegrationId?: number;
    integrationId?: number;
    organizationId: number;
    settings?: {};
    integrationType?: string;
    remoteOrganizationId?: string;
    remoteTaskId?: string;
    remoteTaskMeta?: Record<string, any>;
    taskConnectionId?: number;
    integrationMessageError?: IntegrationMessage;
  };

  const integrationMessageErrorFor = ({
    cardKey,
    integrationId
  }: CardProps) => {
    const normalizedCardKey = cardKey
      .replace(/^custom\./, '')
      .replace(/\./g, '')
      .toLowerCase();
    const latestMessage = integrationMessages.find(message => {
      const normalizedConnectorCode = (message.connector_code || '')
        .replace(/^custom\./, '')
        .replace(/\./g, '')
        .toLowerCase();

      return integrationId !== undefined
        ? message.integration_id === integrationId
        : normalizedConnectorCode === normalizedCardKey;
    });

    return latestMessage?.level === 'error' ? latestMessage : undefined;
  };

  const renderCard = (cardProps: CardProps) => {
    const integrationMessageError = integrationMessageErrorFor(cardProps);

    return (
      <Card
        key={cardProps.cardKey}
        name={cardProps.cardKey}
        container={container}
        projectId={projectId}
        bugherdUrl={bugherdUrl}
        apiDomain={apiDomain}
        isAdminView={isAdminView}
        taskId={taskId}
        taskLocalId={taskLocalId}
        isSmallView={isSmallView}
        {...cardProps}
        loading={!!cardProps.loading || integrationMessagesLoading}
        integrationMessageError={integrationMessageError}
        refreshIntegrationMessages={refreshIntegrationMessages}
      />
    );
  };

  const webIntegrationCards = webIntegrations?.map(({ key, integration }) =>
    renderCard({
      cardKey: key,
      integration,
      showHelp: true,
      organizationId
    })
  );

  const renderCards = () => {
    return (
      <div className={styles.integrationsOuter}>
        {nativeIntegrations.map(integration => {
          const {
            connector: { code },
            remoteTaskId,
            remoteTaskMeta,
            projectIntegration
          } = integration;

          return renderCard({
            cardKey: code,
            projectName: getProjectName(
              code,
              projectIntegration?.remoteProject || '',
              projectIntegration?.remoteProjectName || '',
              projectIntegration?.settings || {}
            ),
            integrationType: 'native',
            projectIntegrationId: projectIntegration?.id,
            integrationId: projectIntegration?.integrationId,
            organizationId,
            hasQueryWorkflow: code === 'jira',
            settings: projectIntegration?.settings || {},
            remoteOrganizationId: integration.remoteOrganizationId,
            taskConnectionId: integration.taskConnectionId,
            remoteTaskId,
            remoteTaskMeta
          });
        })}

        {harvestEnabled &&
          harvestEmbedUrl &&
          renderCard({
            cardKey: 'harvest',
            harvestEmbedUrl,
            organizationId
          })}

        {webIntegrationCards}
      </div>
    );
  };

  const renderEmptyState = () => {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateImage} />
        <span className={styles.firstIntegration}>
          {strings.firstIntegration}
        </span>
        <Button
          type="primary"
          onClick={handleShowSettings}
          className={styles.addIntegration}
        >
          {strings.addIntegration}
        </Button>
      </div>
    );
  };

  return showIntegrations ? renderCards() : renderEmptyState();
};

export default Integrations;
